<script setup>
/**
 * Edit Members of Chat Room - AI Focus
 * Página para gerenciar membros da sala de chat com wizard
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const roomId = computed(() => Number(route.params.roomId));
const room = computed(() =>
  store.getters['chatRooms/getChatRoom'](roomId.value)
);
const agents = computed(() => store.getters['agents/getAgents']);
const currentMembers = computed(() => room.value?.members || []);

const selectedAgents = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);

const steps = computed(() => [
  {
    number: 1,
    title: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_1_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_1_DESC'),
  },
  {
    number: 2,
    title: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_2_TITLE'),
    description: t('CHAT_ROOMS_SETTINGS.EDIT.STEP_2_DESC'),
  },
]);

const currentStep = 2;

// Initialize selected agents with current members
const initializeSelectedAgents = () => {
  selectedAgents.value = currentMembers.value.map(m => m.id);
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('agents/get'),
      store.dispatch('chatRooms/show', roomId.value),
      store.dispatch('chatRooms/getMembers', roomId.value),
    ]);
    initializeSelectedAgents();
  } catch {
    // Silently fail
  } finally {
    isLoading.value = false;
  }
});

watch(
  currentMembers,
  () => {
    initializeSelectedAgents();
  },
  { deep: true }
);

const toggleAgent = agentId => {
  const index = selectedAgents.value.indexOf(agentId);
  if (index === -1) {
    selectedAgents.value.push(agentId);
  } else {
    selectedAgents.value.splice(index, 1);
  }
};

const isSelected = agentId => {
  return selectedAgents.value.includes(agentId);
};

const toggleSelectAll = () => {
  if (selectedAgents.value.length === agents.value.length) {
    selectedAgents.value = [];
  } else {
    selectedAgents.value = agents.value.map(a => a.id);
  }
};

const isAllSelected = computed(() => {
  return (
    agents.value.length > 0 &&
    selectedAgents.value.length === agents.value.length
  );
});

const saveMembers = async () => {
  isSaving.value = true;
  try {
    const currentMemberIds = currentMembers.value.map(m => m.id);

    // Members to add (selected but not currently members)
    const toAdd = selectedAgents.value.filter(
      id => !currentMemberIds.includes(id)
    );

    // Members to remove (currently members but not selected)
    const toRemove = currentMemberIds.filter(
      id => !selectedAgents.value.includes(id)
    );

    // Add new members
    if (toAdd.length > 0) {
      await store.dispatch('chatRooms/addMembers', {
        roomId: roomId.value,
        memberIds: toAdd,
      });
    }

    // Remove unselected members
    await Promise.all(
      toRemove.map(memberId =>
        store.dispatch('chatRooms/removeMember', {
          roomId: roomId.value,
          memberId,
        })
      )
    );

    useAlert(t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_SUCCESS'));
    router.push({ name: 'settings_chat_rooms_list' });
  } catch {
    useAlert(t('CHAT_ROOMS_SETTINGS.MEMBERS.ADD_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const goToEdit = () => {
  router.push({
    name: 'settings_chat_rooms_edit',
    params: { roomId: roomId.value },
  });
};
</script>

<template>
  <div class="flex h-full overflow-auto p-6">
    <div
      v-if="isLoading"
      class="flex items-center justify-center w-full h-full"
    >
      <Spinner />
    </div>
    <div
      v-else
      class="flex w-full max-w-5xl mx-auto border border-n-weak rounded-xl bg-n-solid-2"
    >
      <!-- Left side - Steps -->
      <div class="w-64 flex-shrink-0 p-6 border-r border-n-weak">
        <div class="relative">
          <!-- Connecting line -->
          <div class="absolute left-4 top-8 w-0.5 bg-n-weak step-line" />

          <div class="space-y-6 relative">
            <div
              v-for="step in steps"
              :key="step.number"
              class="flex gap-3 cursor-pointer"
              @click="step.number === 1 ? goToEdit() : null"
            >
              <div
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium flex-shrink-0 z-10"
                :class="
                  step.number === currentStep
                    ? 'bg-woot-500 text-white'
                    : step.number < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-n-solid-3 border border-n-weak text-n-slate-11'
                "
              >
                <fluent-icon
                  v-if="step.number < currentStep"
                  icon="checkmark"
                  size="16"
                />
                <span v-else>{{ step.number }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium"
                  :class="
                    step.number === currentStep
                      ? 'text-woot-500'
                      : step.number < currentStep
                        ? 'text-green-500'
                        : 'text-n-slate-11'
                  "
                >
                  {{ step.title }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ step.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Members table -->
      <div class="flex-1 p-6 overflow-auto">
        <div class="max-w-2xl">
          <h2 class="text-xl font-semibold text-n-slate-12 mb-1">
            {{ t('CHAT_ROOMS_SETTINGS.MEMBERS.EDIT_TITLE') }} - {{ room?.name }}
          </h2>
          <p class="text-sm text-n-slate-11 mb-6">
            {{
              t('CHAT_ROOMS_SETTINGS.MEMBERS.EDIT_DESC', {
                roomName: room?.name,
              })
            }}
          </p>

          <!-- Members table -->
          <div class="border border-n-weak rounded-lg overflow-hidden mb-6">
            <table class="w-full">
              <thead>
                <tr class="bg-n-alpha-1">
                  <th class="w-12 px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      :checked="isAllSelected"
                      class="accent-woot-500"
                      @change="toggleSelectAll"
                    />
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                  >
                    {{ t('AGENT_MGMT.HEADER') }}
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                  >
                    {{ t('PROFILE_SETTINGS.FORM.EMAIL.LABEL') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="agent in agents"
                  :key="agent.id"
                  class="hover:bg-n-alpha-1 cursor-pointer transition-colors"
                  @click="toggleAgent(agent.id)"
                >
                  <td class="px-4 py-3">
                    <input
                      type="checkbox"
                      :checked="isSelected(agent.id)"
                      class="accent-woot-500 pointer-events-none"
                    />
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-3">
                      <Avatar
                        :src="agent.avatar_url"
                        :name="agent.name"
                        :size="32"
                      />
                      <span class="font-medium text-n-slate-12">
                        {{ agent.name }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-sm text-n-slate-11">
                    {{ agent.email }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer with counter and save button -->
          <div class="flex items-center justify-between">
            <p class="text-sm text-n-slate-11">
              {{ selectedAgents.length }} / {{ agents.length }}
            </p>
            <Button
              :label="t('CHAT_ROOMS_SETTINGS.FORM.UPDATE')"
              :is-loading="isSaving"
              @click="saveMembers"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.step-line {
  height: calc(100% - 2rem);
}
</style>
